import {
    ModuleWithProviders,
    NgModule,
    Optional,
    SkipSelf,
    CUSTOM_ELEMENTS_SCHEMA,
    APP_INITIALIZER
} from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { SharedModule } from '../shared/shared.module';

import { CoreRoutingModule } from './core-routing.module';

import { AuthGuard } from './guards/auth.guard';
import { LoginGuard } from './guards/login.guard';
import { DomainGuard } from './guards/domain.guard';
import { SelfRegisterGuard } from './guards/self-register.guard';
import { TooltipModule } from 'ngx-tooltip';
import {NgxPaginationModule} from 'ngx-pagination';

import { SimpleNotificationsModule, SimpleNotificationsComponent } from 'angular2-notifications';
import { ModalModule } from 'angular2-modal';
import { BootstrapModalModule } from 'angular2-modal/plugins/bootstrap';


//  Components
import { MissingComponent } from './components/missing/missing.component';
import { ErrorComponent } from './components/error/error.component';
import { CoursesComponent } from './components/course/courses.component';
import { CourseTileComponent } from './components/course/tile.component';
import { DeliverablesComponent } from './components/deliverable/deliverables.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { RegisterComponent } from './components/register/register.component';
import { RegisterWtihVoucherComponent } from './components/register-with-voucher/register-voucher.component';
import { RedeemVoucherComponent } from './components/redeem/redeem.component';
import { ProfileComponent } from './components/profile/profile.component';

import { SupportComponent } from './components/support/support.component';

import { ScormComponent } from './components/content/scorm/scorm.component';
import { DummyComponent } from './components/content/scorm/dummy/dummy.component';
import { ScormTileComponent } from './components/content/scorm/tile.component';
import { EBookComponent } from './components/content/ebook/ebook.component';
import { EBookTileComponent } from './components/content/ebook/tile.component';
import { ArchiveComponent } from './components/content/archive/archive.component';
import { ArchiveTileComponent } from './components/content/archive/tile.component';
import { ISpringComponent } from './components/content/ispring/ispring.component';
import { ISpringTileComponent } from './components/content/ispring/tile.component';
import { UnknownTileComponent } from './components/content/unknown/tile.component';
import { ClassPreviewComponent } from './components/class/class-preview.component';

//  Services
import { ScormApiService } from './components/content/scorm/scorm.api.service';
import { RequestService } from './services/request.service';
import { ConfigService } from './services/config.service';
import { StorageService } from './services/storage.service';
import { DomainService } from './services/domain.service';
import { AuthService } from './services/auth.service';
import { SessionService } from './services/session.service';
import { NotificationService } from './services/notification.service';
import { EnvironmentService } from './services/environment.service';
import { LicenseService } from './services/license.service';
import { FullscreenService } from './services/fullscreen.service';
import { BrowserEventsService } from './services/browser-events.service';
import { ProfileService } from './components/profile/profile.service';
import { UploadService } from './services/upload.service';

//  Partials
import { NavbarComponent } from './components/partials/navbar/navbar.component';
import { FooterComponent } from './components/partials/footer/footer.component';
import { PageTemplateComponent } from './components/partials/pagetemplate/pagetemplate.component';
import { PageBaseComponent } from './components/partials/pagebase/component';
import { PageBaseTileComponent } from './components/partials/pagebasetile/component';
import { LearningTileComponent } from './components/partials/learningtile/component';
import { ExamComponent } from './components/exam/exam.component';
import { CertificateTileComponent } from './components/content/certificate/certificate.component';
import { TranslateService } from './services/translate.service';
import { SmartModalService } from './services/smart-modal.service';
import { SmartLoaderService } from './services/smart-loader.service';
import { ModalService } from './services/modal.service';
import { CertificateService } from './components/content/certificate/certificate.service';
import { Privacy } from 'tslint/lib/rules/completedDocsRule';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { PrivacyPolicyModalComponent } from '../shared/components/modal/privacy-policy-modal.component';
import { ScormCloudComponent } from './components/content/scormcloud/scorm-cloud.component';
import { ScormCloudTileComponent } from './components/content/scormcloud/tile.component';
import { ScormCloudService } from './services/scorm-cloud.service';
import { EvaluationDeliverablesComponent } from './components/evaluation/deliverable/deliverables.component';
import { EvaluationBaseTileComponent } from './components/evaluation/content/tile.component';
import { EvaluationArchiveTileComponent } from './components/evaluation/content/archive/tile.component';
import { EvaluationCoursesComponent } from './components/evaluation/course/courses.component';
import { EvaluationCourseTileComponent } from './components/evaluation/course/tile.component';
import { EvaluationEBookComponent } from './components/evaluation/content/ebook/ebook.component';
import { EvaluationISpringComponent } from './components/evaluation/content/ispring/ispring.component';
import { EvaluationScormCloudComponent } from './components/evaluation/content/scormcloud/scorm-cloud.component';
import { EvaluationScormCloudTileComponent } from './components/evaluation/content/scormcloud/tile.component';
import { EvaluationScormComponent } from './components/evaluation/content/scorm/scorm.component';
import { EvaluationScormTileComponent } from './components/evaluation/content/scorm/tile.component';
import { EvaluationNavbarComponent } from './components/evaluation/navbar/navbar.component';
import { EvaluationLearningTileComponent } from './components/evaluation/learningtile/component';
import { EvaluationArchiveComponent } from 'app/core/components/evaluation/content/archive/archive.component';
import { EvaluationEBookTileComponent } from './components/evaluation/content/ebook/tile.component';
import { EvaluationISpringTileComponent } from './components/evaluation/content/ispring/tile.component';
import { EvaluationService } from './components/evaluation/evaluation.service';
import { EvaluationScormApiService } from 'app/core/components/evaluation/content/scorm/scorm.api.service';
import { TileComponent } from './components/content/google-slides/tile.component';
import { GoogleSlidesComponent } from './components/content/google-slides/google-slides.component';
import { ClassTileDetailsComponent } from './components/class/class-tile-details/class-tile-details.component';
import { VideoComponent } from './components/content/video/video.component';
import { VideoTileComponent } from './components/content/video/tile.component';
import { VgCoreModule } from 'videogular2/core';
import { VgControlsModule } from 'videogular2/controls';
import { VgOverlayPlayModule } from 'videogular2/overlay-play';
import { VgBufferingModule } from 'videogular2/buffering';
import { ScormCloudPlayerComponent } from './components/scorm-cloud-player/scorm-cloud-player.component';
import { CacheService } from 'app/core/services/cache.service';
import { RedeemNewComponent } from './components/redeem-new/redeem-new.component';
import { RegisterVoucherNewComponent } from './components/register-voucher-new/register-voucher-new.component';
import { RegisterUserComponent } from './components/register-user/register-user.component';
import { CourseStackPresentationComponent } from 'app/core/components/workflow-new/course/course-stack-presentation/course-stack-presentation.component';
import { CourseCardPresentationComponent } from 'app/core/components/workflow-new/course/course-card-presentation/course-card-presentation.component';
import { CourseCardContainerComponent } from 'app/core/components/workflow-new/course/course-card-container/course-card-container.component';
import { CommunicationBoxComponent } from 'app/core/components/workflow-new/common/communication-box/communication-box.component';
import { ClickableWrapperContainerComponent } from 'app/core/components/workflow-new/common/clickable-wrapper-container/clickable-wrapper-container.component';
import { CardConfigurationService } from 'app/core/components/workflow-new/course/configurations/card-configuration.service';
import { MaterialStackPresentationComponent } from './components/workflow-new/material/material-stack-presentation/material-stack-presentation.component';
import { DeliverableTileStateContainerComponent } from './components/workflow-new/material/deliverable-state-container/deliverable-tile-state-container.component';
import { MaterialStackContainerComponent } from './components/workflow-new/material/material-stack-container/material-stack-container.component';
import { MaterialStackContainerService } from 'app/core/components/workflow-new/material/material-stack-container/material-stack-container.service';
import { DeliverableStackPresentationComponent } from './components/workflow-new/material/deliverable-stack-presentation/deliverable-stack-presentation.component';
import { DeliverableTileLoadingComponent } from './components/workflow-new/material/deliverable-tile-loading/deliverable-tile-loading.component';
import { DeliverableTileInvalidComponent } from './components/workflow-new/material/deliverable-tile-invalid/deliverable-tile-invalid.component';
import { DeliverableTileUptodateComponent } from './components/workflow-new/material/deliverable-tile-uptodate/deliverable-tile-uptodate.component';
import { DeliverableStatePresentationComponent } from './components/workflow-new/material/deliverable-state-presentation/deliverable-state-presentation.component';
import { ActionsComponent } from './components/workflow-new/common/actions/actions.component';
import { MaterialDetailsComponent } from 'app/core/components/workflow-new/material/material-details/material-details.component';
import { InnerCardPresentationComponent } from 'app/core/components/workflow-new/course/inner-card-presentation/inner-card-presentation.component';
import { CardDetailsComponent } from 'app/core/components/workflow-new/course/card-details/card-details.component';
import { MaterialConfigurationService } from 'app/core/components/workflow-new/material/configurations/material-configuration.service';
import { CardPresentationComponent } from 'app/core/components/workflow-new/common/card-presentation';
import { CardDetailsPresentationComponent } from 'app/core/components/workflow-new/course/card-details-presentation/card-details-presentation.component';
import { InnerCourseCardComponent } from 'app/core/components/workflow-new/common/banner-card/inner-course-card/inner-course-card.component';
import { BannerCardComponent } from 'app/core/components/workflow-new/common/banner-card/banner-card.component';
import { CardStackPresentationComponent } from './components/workflow-new/common/card-stack-presentation/card-stack-presentation.component';
import { CourseContainerComponent } from 'app/core/components/workflow-new/course/course-container/course-container.component';
import { PageNotFoundComponent } from './components/workflow-new/page-not-found/page-not-found.component';
import { MultiContentDeliverableListComponent } from './components/workflow-new/material/multi-content-deliverable-list/multi-content-deliverable-list.component';
import { MultiContentDeliverableComponent } from './components/workflow-new/material/multi-content-deliverable/multi-content-deliverable.component';
import { MultiContentDeliverableUpToDateComponent } from './components/workflow-new/material/multi-content-deliverable-up-to-date/multi-content-deliverable-up-to-date.component';
import { ExpandableTileComponent } from './components/workflow-new/material/expandable-tile/expandable-tile.component';
import { ContentTileLoadingComponent } from './components/workflow-new/material/content-tile-loading/content-tile-loading.component';
import { ContentTileInvalidComponent } from './components/workflow-new/material/content-tile-invalid/content-tile-invalid.component';
import { EvaluationMaterialsComponent } from 'app/core/components/evaluation/workflow_new/evaluation-materials/evaluation-materials.component';
import { EvaluationMultiContentDeliverableComponent }
    from 'app/core/components/evaluation/workflow_new/evaluation-multi-content-deliverable/evaluation-multi-content-deliverable.component';
import { EvaluationMultiContentDeliverableListComponent }
    from 'app/core/components/evaluation/workflow_new/evaluation-multi-content-deliverable-list/evaluation-multi-content-deliverable-list.component';
import { EvaluationMaterialStackPresentationComponent }
    from 'app/core/components/evaluation/workflow_new/evaluation-material-stack-presentation/evaluation-material-stack-presentation.component';
import { EvaluationExpandableTileComponent } from 'app/core/components/evaluation/workflow_new/evaluation-expandable-tile/evaluation-expandable-tile.component';
import { EvaluationDeliverableTileUptodateComponent }
    from 'app/core/components/evaluation/workflow_new/evaluation-deliverable-tile-uptodate/evaluation-deliverable-tile-uptodate.component';
import { EvaluationMultiContentDeliverableUpToDateComponent }
    from 'app/core/components/evaluation/workflow_new/evaluation-multi-content-deliverable-up-to-date/evaluation-multi-content-deliverable-up-to-date.component';
import { EvaluationVideoTileComponent } from 'app/core/components/evaluation/content/video/tile.component';
import { EvaluationVideoComponent } from 'app/core/components/evaluation/content/video/video.component';
import { EvaluationGoogleSlidesComponent } from 'app/core/components/evaluation/content/google-slides/google-slides.component';
import { CatalogComponent } from './components/workflow-new/catalog/catalog.component';
import { CatalogProductsService } from './services/catalog-products.service';
import { SelfRegisterComponent } from './components/self-register/self-register.component';
import { LoginPromptComponent } from './components/login-prompt/login-prompt.component';
import { AppLoadService } from './services/app-load.service';
import { RedirectService } from './services/redirect.service';
import { LxpProductLandingComponent } from './components/workflow-new/material/lxp-product-landing/lxp-product-landing.component';
import { ConsumptionRedirectComponent } from './components/workflow-new/consumption-redirect/consumption-redirect.component';
import { LxpCourseLandingComponent } from './components/lxp-course-landing/lxp-course-landing.component';
import { EventListingComponent } from './components/workflow-new/event-listing/event-listing.component';
import { SelfRegisterValidateComponent } from './components/self-register/self-register-validate.component';

export function appLoadFactory(ds: AppLoadService) {
    return ds.returnPromise
}

@NgModule({
    imports: [
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        SharedModule,
        CoreRoutingModule,
        ModalModule.forRoot(),
        BootstrapModalModule,
        SimpleNotificationsModule.forRoot(),
        VgCoreModule,
        VgControlsModule,
        VgOverlayPlayModule,
        VgBufferingModule,
        TooltipModule,
        NgxPaginationModule
    ],
    declarations: [
        NavbarComponent,
        FooterComponent,
        PageTemplateComponent,
        PageBaseComponent,
        PageBaseTileComponent,
        LearningTileComponent,
        SupportComponent,
        MissingComponent,
        ErrorComponent,
        CoursesComponent,
        CourseTileComponent,
        CertificateTileComponent,
        DeliverablesComponent,
        ScormTileComponent,
        DummyComponent,
        EBookTileComponent,
        ISpringTileComponent,
        ArchiveTileComponent,
        UnknownTileComponent,
        WelcomeComponent,
        RegisterComponent,
        RegisterWtihVoucherComponent,
        RedeemVoucherComponent,
        ProfileComponent,
        ClassPreviewComponent,
        ScormComponent,
        EBookComponent,
        ArchiveComponent,
        ISpringComponent,
        ExamComponent,
        PrivacyPolicyComponent,
        ScormCloudComponent,
        ScormCloudTileComponent,
        EvaluationDeliverablesComponent,
        EvaluationBaseTileComponent,
        EvaluationArchiveTileComponent,
        EvaluationCoursesComponent,
        EvaluationCourseTileComponent,
        EvaluationEBookComponent,
        EvaluationEBookTileComponent,
        EvaluationISpringComponent,
        EvaluationISpringTileComponent,
        EvaluationScormCloudComponent,
        EvaluationScormCloudTileComponent,
        EvaluationScormComponent,
        EvaluationScormTileComponent,
        EvaluationVideoTileComponent,
        EvaluationNavbarComponent,
        EvaluationLearningTileComponent,
        EvaluationArchiveComponent,
        EvaluationGoogleSlidesComponent,
        TileComponent,
        GoogleSlidesComponent,
        ClassTileDetailsComponent,
        VideoComponent,
        VideoTileComponent,
        BannerCardComponent,
        InnerCourseCardComponent,
        CardDetailsComponent,
        MaterialDetailsComponent,
        ScormCloudPlayerComponent,
        RedeemNewComponent,
        RegisterVoucherNewComponent,
        RegisterUserComponent,
        CourseStackPresentationComponent,
        CourseCardPresentationComponent,
        CourseCardContainerComponent,
        CommunicationBoxComponent,
        ClickableWrapperContainerComponent,
        InnerCardPresentationComponent,
        CardDetailsPresentationComponent,
        MaterialStackPresentationComponent,
        DeliverableTileStateContainerComponent,
        MaterialStackContainerComponent,
        DeliverableStackPresentationComponent,
        DeliverableTileLoadingComponent,
        DeliverableTileInvalidComponent,
        DeliverableTileUptodateComponent,
        DeliverableStatePresentationComponent,
        ActionsComponent,
        CardPresentationComponent,
        CourseContainerComponent,
        CardStackPresentationComponent,
        PageNotFoundComponent,
        MultiContentDeliverableListComponent,
        MultiContentDeliverableComponent,
        MultiContentDeliverableUpToDateComponent,
        ExpandableTileComponent,
        ContentTileLoadingComponent,
        ContentTileInvalidComponent,
        EvaluationMaterialsComponent,
        EvaluationMultiContentDeliverableComponent,
        EvaluationMultiContentDeliverableListComponent,
        EvaluationMaterialStackPresentationComponent,
        EvaluationExpandableTileComponent,
        EvaluationDeliverableTileUptodateComponent,
        EvaluationMultiContentDeliverableUpToDateComponent,
        EvaluationVideoComponent,
        CatalogComponent,
        SelfRegisterComponent,
        LoginPromptComponent,
        LxpProductLandingComponent,
        ConsumptionRedirectComponent,
        LxpCourseLandingComponent,
        EventListingComponent,
        SelfRegisterValidateComponent
    ],
    exports: [
        NavbarComponent,
        FooterComponent,
        PageTemplateComponent,
        PageBaseComponent,
        PageBaseTileComponent,
        LearningTileComponent,
        CourseTileComponent,
        ScormTileComponent,
        EBookTileComponent,
        ISpringTileComponent,
        ArchiveTileComponent,
        UnknownTileComponent,
        VideoTileComponent,
        EvaluationNavbarComponent,
        EvaluationLearningTileComponent,
        EvaluationCourseTileComponent,
        EvaluationScormTileComponent,
        EvaluationEBookTileComponent,
        EvaluationISpringTileComponent,
        EvaluationArchiveTileComponent,
        EvaluationVideoTileComponent,
        SimpleNotificationsComponent,
        EvaluationGoogleSlidesComponent
    ],
    providers: [
        AppLoadService,
        DomainService,
        RequestService,
        { provide: APP_INITIALIZER, useFactory: domainService, deps: [DomainService], multi: true },
        AuthGuard,
        LoginGuard,
        DomainGuard,
        SelfRegisterGuard,
        ConfigService,
        StorageService,
        AuthService,
        SessionService,
        EnvironmentService,
        NotificationService,
        LicenseService,
        FullscreenService,
        BrowserEventsService,
        ScormApiService,
        TranslateService,
        ProfileService,
        UploadService,
        ModalService,
        SmartLoaderService,
        SmartModalService,
        CertificateService,
        ScormCloudService,
        EvaluationService,
        EvaluationScormApiService,
        CardConfigurationService,
        MaterialConfigurationService,
        CacheService,
        MaterialStackContainerService,
        RedirectService,
        CatalogProductsService,
        {
            provide: APP_INITIALIZER,
            useFactory: appLoadFactory,
            deps: [AppLoadService],
            multi: true
        }
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
export class CoreModule {

    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error(
                'CoreModule is already loaded. Import it in the AppModule only');
        }
    }

    static forRoot(): ModuleWithProviders {
        return {
            ngModule: CoreModule,
            providers: [

            ]
        };
    }
}

export function domainService(domainService: DomainService) { return () => domainService.init(); };



// WEBPACK FOOTER //
// ./src/app/core/core.module.ts